import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import yetkinlikImg from '../assets/yetkinlik.png?w=563&format=webp';
import yetkinlikSrcSet from '../assets/yetkinlik.png?w=300;563&format=webp&as=srcset';

const Competencies = () => {
  const { t } = useTranslation();
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
    const leftObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setLeftVisible(true);
      },
      { threshold: 0.2 }
    );

    const rightObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setRightVisible(true);
      },
      { threshold: 0.2 }
    );

    if (leftRef.current) leftObserver.observe(leftRef.current);
    if (rightRef.current) rightObserver.observe(rightRef.current);

    return () => {
      leftObserver.disconnect();
      rightObserver.disconnect();
    };
  }, []);

  const allLanguages = [
    { key: 'turkish', flag: '🇹🇷' },
    { key: 'english', flag: '🇬🇧' },
    { key: 'german', flag: '🇩🇪' },
    { key: 'french', flag: '🇫🇷' },
    { key: 'spanish', flag: '🇪🇸' },
    { key: 'italian', flag: '🇮🇹' },
    { key: 'portuguese', flag: '🇵🇹' },
    { key: 'dutch', flag: '🇳🇱' },
    { key: 'polish', flag: '🇵🇱' },
    { key: 'czech', flag: '🇨🇿' },
    { key: 'swedish', flag: '🇸🇪' },
    { key: 'norwegian', flag: '🇳🇴' },
    { key: 'danish', flag: '🇩🇰' },
    { key: 'romanian', flag: '🇷🇴' },
    { key: 'hungarian', flag: '🇭🇺' },
    { key: 'finnish', flag: '🇫🇮' },
    { key: 'croatian', flag: '🇭🇷' },
    { key: 'slovenian', flag: '🇸🇮' },
    { key: 'slovak', flag: '🇸🇰' },
    { key: 'estonian', flag: '🇪🇪' },
    { key: 'latvian', flag: '🇱🇻' },
    { key: 'lithuanian', flag: '🇱🇹' },
    { key: 'albanian', flag: '🇦🇱' },
    { key: 'bosnian', flag: '🇧🇦' },
    { key: 'macedonian', flag: '🇲🇰' },
    { key: 'azerbaijani', flag: '🇦🇿' },
    { key: 'indonesian', flag: '🇮🇩' },
    { key: 'malay', flag: '🇲🇾' },
    { key: 'maori', flag: '🇳🇿' },
    { key: 'tagalog', flag: '🇵🇭' },
    { key: 'vietnamese', flag: '🇻🇳' },
  ];

  // Mobilde sadece ilk 12 dili göster, desktop'ta hepsini göster
  const mobileLanguagesCount = 12;
  const languages = allLanguages;

  return (
    <section className="section-spacing bg-gray-50 overflow-x-hidden">
      <div className="container-custom px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-x-8 sm:gap-x-12 md:gap-x-16 gap-y-6 sm:gap-y-8 items-start">
          {/* Competencies */}
          <div 
            ref={leftRef}
            className={`transition-all duration-700 ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-gray-900 mb-3 sm:mb-4">
              {t('competencies.title')}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
              {t('competencies.subtitle')}
            </p>
            
            <div className={`bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-card transition-all duration-500 ${
              leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`} style={{ transitionDelay: '200ms' }}>
              <img
                src={yetkinlikImg}
                srcSet={yetkinlikSrcSet}
                sizes="(max-width: 640px) calc(100vw - 3rem), 563px"
                alt={t('competencies.imageAlt')}
                className="w-full h-auto object-cover rounded-lg"
                width="563"
                height="304"
                loading="lazy"
                decoding="async"
              />
              <p className="text-xs text-gray-500 text-left mt-3 sm:mt-5">
                {`${t('competencies.analysisTitle')} — ${t('competencies.analysisNote')}`}
              </p>
            </div>
          </div>

          {/* Languages */}
          <div 
            ref={rightRef}
            className={`transition-all duration-700 ${
              rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-gray-900 mb-3 sm:mb-4">
              {t('competencies.languagesTitle')}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
              {t('competencies.languagesSubtitle')}
            </p>
            
            <div className={`bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-card transition-all duration-500 ${
              rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`} style={{ transitionDelay: '200ms' }}>
              {/* Mobilde sadece ilk 12 dil, desktop'ta hepsi */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                {/* Mobilde gösterilecek diller (ilk 12) */}
                {languages.slice(0, mobileLanguagesCount).map((lang, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-xs font-label text-gray-700 bg-gray-50 hover:bg-accent-teal/10 hover:border-accent-teal/30 transition-all duration-300 ${
                      rightVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    }`}
                    style={{ transitionDelay: `${300 + index * 20}ms` }}
                  >
                    <span className="mr-1.5 text-base leading-none">{lang.flag}</span>
                    <span>
                      {t(`competencies.${lang.key}`)}
                    </span>
                  </span>
                ))}
                
                {/* Desktop'ta gösterilecek ek diller */}
                {languages.slice(mobileLanguagesCount).map((lang, index) => (
                  <span
                    key={mobileLanguagesCount + index}
                    className={`hidden md:inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-xs font-label text-gray-700 bg-gray-50 hover:bg-accent-teal/10 hover:border-accent-teal/30 transition-all duration-300 ${
                      rightVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    }`}
                    style={{ transitionDelay: `${300 + (mobileLanguagesCount + index) * 20}ms` }}
                  >
                    <span className="mr-1.5 text-base leading-none">{lang.flag}</span>
                    <span>
                      {t(`competencies.${lang.key}`)}
                    </span>
                  </span>
                ))}
                
              </div>
              <p className="text-xs text-gray-500">
                {t('competencies.languagesNote')}
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Competencies;
